package com.arena_web.ui.components.dropdown

import androidx.compose.runtime.*
import com.arena_web.data.dto.SportDto
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.theme.SportColorScheme
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.utils.m
import com.arena_web.ui.utils.mBorderRadius
import com.arena_web.ui.utils.mFontSize
import com.arena_web.ui.utils.mGap
import com.arena_web.ui.utils.mHeight
import com.arena_web.ui.utils.mLetterSpacing
import com.arena_web.ui.utils.mLineHeight
import com.arena_web.ui.utils.mPadding
import com.arena_web.ui.utils.mPaddingLeft
import com.arena_web.ui.utils.mPaddingRight
import com.arena_web.ui.utils.mSize
import com.arena_web.ui.utils.mWidth
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText

/** Canonical display order; unlisted sports follow, "Other" is always last. */
private val SPORT_DISPLAY_ORDER = listOf("football", "tennis", "basketball", "volleyball", "padel")

/** Fixed sub-list revealed under "Other" (customSportName values we seed). */
val CUSTOM_OTHER_SPORTS = listOf(
    "Karting", "Bowling", "Climbing", "Golf", "Billiards", "Laser Tag", "Paintball", "Table Tennis",
)

/** Monochrome glyph (mask-tintable SVG) per custom sport, matching the main
 *  sport-icon style. */
fun customOtherSportIcon(name: String): String? = when (name) {
    "Karting" -> AppIcons.OtherKarting
    "Bowling" -> AppIcons.OtherBowling
    "Climbing" -> AppIcons.OtherClimbing
    "Golf" -> AppIcons.OtherGolf
    "Billiards" -> AppIcons.OtherBilliards
    "Laser Tag" -> AppIcons.OtherLaserTag
    "Paintball" -> AppIcons.OtherPaintball
    "Table Tennis" -> AppIcons.OtherTableTennis
    else -> null
}

/**
 * Shared sport-picker dropdown panel — mirrors the mobile app's
 * `SportDropdown` (ArenaTopBar.kt) 1:1: icon-in-circle rows, per-sport
 * accent colours, a hairline divider before the icon-less "Other" row and
 * the implicit "All" logic — there is no "All" row; re-tapping the active
 * sport deselects it back to the synthetic "All" pseudo-sport (id="").
 *
 * One source of truth for both the mobile top bar and the desktop AppHeader.
 * Sizes use the project's `m()` helpers which clamp at 1 px above the 390-px
 * mobile baseline, so the panel renders at design size on desktop and scales
 * down on narrower phones.
 *
 * Caller is responsible for positioning (e.g. wrapping in a `position: absolute`
 * container or a Modifier with absolute anchors).
 */
@Composable
fun SportDropdownPanel(
    selectedSportName: String,
    sportsList: List<SportDto>,
    onSelect: (SportDto) -> Unit,
    modifier: Modifier = Modifier,
    /** Currently selected custom sport under "Other" (null = all Other). */
    selectedOtherSport: String? = null,
    /** Fired when a custom sport (or "All" = null) is chosen under "Other". */
    onSelectOtherSport: (String?) -> Unit = {},
) {
    Box(
        modifier = Modifier
            .mBorderRadius(26)
            .mPadding(v = 12, h = 0)
            .styleModifier {
                property("width", m(200))
                // Gradient hairline that follows the radius: panel fill on the
                // padding-box, SportDropdownBorder on the border-box.
                property("border", "1px solid transparent")
                property(
                    "background",
                    "linear-gradient(var(--l-s2, #09142d), var(--l-s2, #09142d)) padding-box, " +
                        "${AppGradients.SportDropdownBorder} border-box"
                )
                property("box-shadow", "0 ${m(8)} ${m(16)} rgba(0, 0, 0, 0.35)")
                // Never extend past the bottom navigation: cap to the viewport
                // minus the top-bar offset + bottom-nav clearance, scroll inside.
                property("max-height", "calc(100dvh - ${m(180)})")
                property("overflow-y", "auto")
                property("overscroll-behavior", "contain")
                property("scrollbar-width", "thin")
                property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
            }
            .then(modifier)
    ) {
        Column(modifier = Modifier.fillMaxWidth().mGap(8)) {
            // Deselect target: no sport filter. Same synthetic DTO the storage
            // layer already understands (id="" → "All").
            val allOption = SportDto(
                id = "",
                name = "All",
                icon = "",
                maxPlayersPerTeam = 0,
                minPlayersPerTeam = 0,
                createdAt = "",
                updatedAt = "",
            )
            val orderedSports = sportsList.sortedBy { sport ->
                val index = SPORT_DISPLAY_ORDER.indexOf(sport.name.lowercase())
                when {
                    index >= 0 -> index
                    sport.name.equals("Other", ignoreCase = true) -> SPORT_DISPLAY_ORDER.size + 1
                    else -> SPORT_DISPLAY_ORDER.size
                }
            }
            // News has no per-"Other" sub-sports (Karting/Golf/… carry no
            // articles), so there "Other" is a flat catch-all filter — the
            // sub-list never expands. Elsewhere it expands as before.
            val onNewsPage = kotlinx.browser.window.location.pathname.startsWith("/news")
            // "Other" starts expanded when a custom sport is already active.
            var otherExpanded by remember { mutableStateOf(selectedOtherSport != null && !onNewsPage) }
            orderedSports.forEachIndexed { index, sport ->
                val isOther = sport.name.equals("other", ignoreCase = true)
                val isSelected = sport.name.equals(selectedSportName, ignoreCase = true)
                if (isOther) {
                    OtherExpandableRow(
                        sport = sport,
                        isSelected = isSelected,
                        expanded = otherExpanded,
                        expandable = !onNewsPage,
                        selectedOtherSport = selectedOtherSport,
                        bottomInset = index < orderedSports.lastIndex,
                        onToggle = { otherExpanded = !otherExpanded },
                        // Persist the custom sport BEFORE onSelect (which fires the
                        // caller's reload) so the new filter reads the fresh value.
                        onSelectAllOther = { onSelectOtherSport(null); onSelect(sport) },
                        onSelectCustom = { name -> onSelectOtherSport(name); onSelect(sport) },
                    )
                } else {
                    SportDropdownPanelRow(
                        sport = sport,
                        isSelected = isSelected,
                        bottomInset = index < orderedSports.lastIndex,
                        // "Section Underline" hairline closes the item before "Other".
                        bottomDivider = orderedSports.getOrNull(index + 1)
                            ?.name?.equals("other", ignoreCase = true) == true,
                        // Re-tapping the active sport deselects it back to "All".
                        onSelect = { onSelect(if (isSelected) allOption else sport) },
                    )
                }
            }
        }
    }
}

@Composable
private fun SportDropdownPanelRow(
    sport: SportDto,
    isSelected: Boolean,
    bottomInset: Boolean,
    bottomDivider: Boolean,
    onSelect: () -> Unit,
) {
    val colors = SportColors.forSport(sport.name.lowercase())
    // Selected row: white on the dark panel, dark on the light panel.
    val textColor =
        if (isSelected) org.jetbrains.compose.web.css.Color("var(--l-text-strong, #FFFFFF)")
        else com.arena_web.ui.theme.ThemeVars.Text2
    // "Other" is a text-only row in the design; unknown sports keep a fallback glyph.
    val glyph = when (sport.name.lowercase()) {
        "football" -> AppIcons.SportFootball
        "basketball" -> AppIcons.SportBasketball
        "volleyball" -> AppIcons.SportVolleyball
        "tennis" -> AppIcons.SportTennis
        "padel" -> AppIcons.SportPadel
        "other" -> null
        else -> AppIcons.SportOther
    }
    val textModifier = Modifier
        .mFontSize(16)
        .mLineHeight(24)
        .mLetterSpacing(0.16)
        .fontWeight(400)
        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
        .color(textColor)
        .styleModifier { property("white-space", "nowrap") }
    Column(modifier = Modifier.fillMaxWidth()) {
        if (glyph == null) {
            // Icon-less "Other": label centred in the row, check pinned to the end.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .mPaddingLeft(12)
                    .mPaddingRight(16)
                    .cursor(Cursor.Pointer)
                    .onClick { onSelect() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(sport.name.replaceFirstChar { it.uppercase() }, modifier = textModifier)
                if (isSelected) {
                    CheckGlyph(colors, Modifier.align(Alignment.CenterEnd))
                }
            }
        } else {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .mPaddingLeft(12)
                    .mPaddingRight(16)
                    .mGap(8)
                    .cursor(Cursor.Pointer)
                    .onClick { onSelect() },
                verticalAlignment = Alignment.CenterVertically
            ) {
                SportIconCircle(glyph, isSelected, colors)
                SpanText(
                    sport.name.replaceFirstChar { it.uppercase() },
                    modifier = textModifier.flexGrow(1)
                )
                if (isSelected) {
                    CheckGlyph(colors)
                }
            }
        }
        if (bottomInset) {
            Box(modifier = Modifier.mHeight(8))
        }
        if (bottomDivider) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("height", m(0.8))
                        property("background", AppGradients.PanelDivider)
                    }
            )
        }
    }
}

/**
 * "Other" row that expands into the fixed [CUSTOM_OTHER_SPORTS] sub-list.
 * Tapping the header toggles the list; tapping a child selects that custom
 * sport (or "All" for every Other venue). Mirrors the text-only "Other" style.
 */
@Composable
private fun OtherExpandableRow(
    sport: SportDto,
    isSelected: Boolean,
    expanded: Boolean,
    expandable: Boolean,
    selectedOtherSport: String?,
    bottomInset: Boolean,
    onToggle: () -> Unit,
    onSelectAllOther: () -> Unit,
    onSelectCustom: (String) -> Unit,
) {
    val colors = SportColors.forSport("other")
    fun rowText(active: Boolean) = Modifier
        .mFontSize(16).mLineHeight(24).mLetterSpacing(0.16).fontWeight(400)
        .fontFamily(AppTypography.PRIMARY_FONT_NAME, AppTypography.PRIMARY_FONT_FALLBACK)
        .color(if (active) org.jetbrains.compose.web.css.Color("var(--l-text-strong, #FFFFFF)") else com.arena_web.ui.theme.ThemeVars.Text2)
        .styleModifier { property("white-space", "nowrap") }

    Column(modifier = Modifier.fillMaxWidth()) {
        // Header: "Other". When expandable, a chevron toggles the sub-list;
        // when flat (news), tapping selects the "Other" catch-all directly.
        Box(
            modifier = Modifier.fillMaxWidth().mPaddingLeft(12).mPaddingRight(16)
                .cursor(Cursor.Pointer).onClick { if (expandable) onToggle() else onSelectAllOther() },
            contentAlignment = Alignment.Center
        ) {
            SpanText(sport.name.replaceFirstChar { it.uppercase() }, modifier = rowText(isSelected))
            if (expandable) {
                SpanText(
                    if (expanded) "▾" else "▸",
                    modifier = Modifier.align(Alignment.CenterEnd).mFontSize(12)
                        .color(com.arena_web.ui.theme.ThemeVars.Text2)
                )
            } else if (isSelected) {
                CheckGlyph(colors, Modifier.align(Alignment.CenterEnd))
            }
        }
        if (expandable && expanded) {
            val items = listOf<Pair<String, String?>>("All" to null) +
                CUSTOM_OTHER_SPORTS.map { it to it }
            items.forEach { (label, value) ->
                val active = isSelected && selectedOtherSport == value
                Box(modifier = Modifier.mHeight(6))
                Box(
                    modifier = Modifier.fillMaxWidth().mPaddingLeft(20).mPaddingRight(16)
                        .cursor(Cursor.Pointer)
                        .onClick { if (value == null) onSelectAllOther() else onSelectCustom(value) },
                    contentAlignment = Alignment.CenterStart
                ) {
                    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.mGap(8)) {
                        // Monochrome glyph, tinted like the main sport icons
                        // (text-primary idle, sport colour when active). "All"
                        // reserves the same width with no glyph.
                        val glyphSrc = if (value == null) AppIcons.OtherAll else customOtherSportIcon(value)
                        val tint = if (active) org.jetbrains.compose.web.css.Color(colors.primary.toString()) else com.arena_web.ui.theme.ThemeVars.Text1
                        Box(modifier = Modifier.mSize(18)) {
                            if (glyphSrc != null) {
                                Box(
                                    modifier = Modifier.mSize(18).styleModifier {
                                        property("background-color", tint.toString())
                                        property("-webkit-mask-image", "url(\"$glyphSrc\")")
                                        property("mask-image", "url(\"$glyphSrc\")")
                                        property("-webkit-mask-size", "contain")
                                        property("mask-size", "contain")
                                        property("-webkit-mask-repeat", "no-repeat")
                                        property("mask-repeat", "no-repeat")
                                        property("-webkit-mask-position", "center")
                                        property("mask-position", "center")
                                    }
                                )
                            }
                        }
                        SpanText(label, modifier = rowText(active))
                    }
                    if (active) CheckGlyph(colors, Modifier.align(Alignment.CenterEnd))
                }
            }
            // Extra breathing room after the last sub-type.
            Box(modifier = Modifier.mHeight(14))
        }
        if (bottomInset) {
            Box(modifier = Modifier.mHeight(8))
        }
    }
}

/**
 * 32px sport-icon circle: gradient fill + hairline ring when idle, solid
 * sport-primary fill when selected — the mobile app's icon-circle recipe.
 */
@Composable
private fun SportIconCircle(
    glyph: String,
    isSelected: Boolean,
    colors: SportColorScheme,
) {
    Box(
        modifier = Modifier
            .mSize(32)
            .flexShrink(0)
            .styleModifier {
                property("position", "relative")
                property("border-radius", "50%")
                property(
                    "background",
                    if (isSelected) colors.primary.toString() else AppGradients.SportIconCircleFill
                )
            },
        contentAlignment = Alignment.Center
    ) {
        if (!isSelected) {
            // Gradient hairline ring — border-box gradient masked to the border
            // area only (same mask-composite trick as the section cards).
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .styleModifier {
                        property("position", "absolute")
                        property("inset", "0")
                        property("border", "0.3px solid transparent")
                        property("border-radius", "50%")
                        property("background", "${AppGradients.SportIconCircleBorder} border-box")
                        property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                        property("-webkit-mask-composite", "xor")
                        property("mask-composite", "exclude")
                        property("pointer-events", "none")
                    }
            )
        }
        // Mask-image instead of <img> so the glyph is tintable: idle rows use
        // the shared text-primary blue, the selected row flips to the sport's
        // on-primary colour (white on football blue, black on the bright ones).
        val tint = if (isSelected) org.jetbrains.compose.web.css.Color(colors.onPrimary.toString()) else com.arena_web.ui.theme.ThemeVars.Text1
        Box(
            modifier = Modifier
                .mSize(18)
                .styleModifier {
                    property("background-color", tint.toString())
                    property("-webkit-mask-image", "url(\"$glyph\")")
                    property("mask-image", "url(\"$glyph\")")
                    property("-webkit-mask-size", "contain")
                    property("mask-size", "contain")
                    property("-webkit-mask-repeat", "no-repeat")
                    property("mask-repeat", "no-repeat")
                    property("-webkit-mask-position", "center")
                    property("mask-position", "center")
                }
        )
    }
}

/** 16px checkmark tinted with the sport's primary colour. */
@Composable
private fun CheckGlyph(colors: SportColorScheme, modifier: Modifier = Modifier) {
    Box(
        modifier = Modifier
            .mSize(16)
            .flexShrink(0)
            .styleModifier {
                property("background-color", colors.primary.toString())
                property("-webkit-mask-image", "url(\"${AppIcons.Check}\")")
                property("mask-image", "url(\"${AppIcons.Check}\")")
                property("-webkit-mask-size", "contain")
                property("mask-size", "contain")
                property("-webkit-mask-repeat", "no-repeat")
                property("mask-repeat", "no-repeat")
                property("-webkit-mask-position", "center")
                property("mask-position", "center")
            }
            .then(modifier)
    )
}
